import { useCallback } from 'react';
import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import { api } from 'config/api';

const growLog = logger.forDomain('IAM', { subdomain: 'authentication' });
const tracer = trace.getTracer('@parrot-software/observability');

interface SSOResponse {
  redirectUrl: string;
  expiresIn: number;
}

export interface GrowSSOOptions {
  redirect_to?: string;
  target?: 'same-tab' | 'new-tab';
}

/** Trades a short-lived handoff JWT for Grow's SSO callback URL. It is spent on arrival, so it is never cached. */
export const useGrowSSO = () => {
  return useCallback(async ({ redirect_to = '/', target = 'new-tab' }: GrowSSOOptions = {}) => {
    // Opened before the await so it still runs inside the user gesture and the popup blocker lets it
    // through; its URL is set once the redirect is known.
    const placeholder = target === 'new-tab' ? window.open('about:blank', '_blank') : null;

    const span = tracer.startSpan('IAM.grow_sso');
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      growLog.info('growSsoAttempt', { 'grow.redirectTo': redirect_to, 'grow.target': target });

      try {
        const { data } = await api.get<SSOResponse>('/v1/auth/sso/grow/', {
          params: { redirect_to },
        });

        const redirectUrl = data?.redirectUrl;
        if (!redirectUrl || typeof redirectUrl !== 'string') {
          throw new Error(`Grow SSO response missing "redirectUrl". Received: ${JSON.stringify(data)}`);
        }

        growLog.info('growSsoResponseReceived', { 'grow.expiresIn': data.expiresIn });

        if (target === 'new-tab' && placeholder) {
          placeholder.location.href = redirectUrl;
          return;
        }

        if (target === 'new-tab') {
          growLog.warn('growSsoPopupFallback');
        }

        window.location.href = redirectUrl;
      } catch (error) {
        if (placeholder) placeholder.close();
        growLog.error('growSsoRequest', {
          'error.kind': (error as any)?.constructor?.name || 'GrowSSOError',
          'error.message': (error as any)?.message,
          'http.status_code': (error as any)?.response?.status,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  }, []);
};
